import { authMiddlewares } from '@/middlewares/auth.middlewares';
import { ecommerceMiddlewares } from '@/middlewares/ecommerce.middlewares';
import { EcommerceSettingsPlanType } from '@/interfaces/ecommerce.interface';
import { useEcommerceStore } from '@/stores/ecommerce.store';
import type { RouteRecordRaw } from 'vue-router';

export default [
  {
    path: '',
    component: () => import('@/modules/flokee/layouts/DashboardLayout.vue'),
    beforeEnter: [
      authMiddlewares.isAuthenticated,
      authMiddlewares.isAccountConfigured,
      authMiddlewares.enforceBusinessUnit,
    ],
    children: [
      {
        path: '',
        name: 'DashboardView',
        meta: { title: 'nav_menu.dashboard' },
        component: () => import('@/modules/flokee/views/common/DashboardView.vue'),
      },
      {
        path: 'dashboard/ecommerce',
        name: 'EcommerceDashboardView',
        meta: { title: 'nav_menu.ecommerce_dashboard' },
        component: () => import('@/modules/flokee/views/common/EcommerceDashboardView.vue'),
      },
      {
        path: 'dashboard/ecommerce/whatsapp',
        name: 'WhatsappAnalyticsView',
        meta: { title: 'nav_menu.whatsapp_analytics' },
        component: () => import('@/modules/flokee/views/common/WhatsappAnalyticsView.vue'),
      },
      {
        path: 'products',
        name: 'ProductsView',
        meta: { title: 'nav_menu.products' },
        component: () => import('@/modules/flokee/views/common/ProductsView.vue'),
      },
      {
        path: 'sales',
        name: 'SalesView',
        meta: { title: 'nav_menu.sales' },
        component: () => import('@/modules/flokee/views/common/SalesView.vue'),
      },
    ],
  },
  {
    path: 'invest',
    component: () => import('@/modules/flokee/layouts/DashboardBlankLayout.vue'),
    meta: { title: 'nav_menu.investment' },
    beforeEnter: [
      authMiddlewares.isAuthenticated,
      authMiddlewares.isAccountConfigured,
      authMiddlewares.enforceBusinessUnit,
      (to, from, next) => {
        const ecommerceStore = useEcommerceStore();
        if (
          ecommerceStore.currentEcommerce?.settings.flokee_plan_type ===
          EcommerceSettingsPlanType.PAID
        ) {
          return next({ name: 'AccountSettingsPlanView' });
        }
        return next();
      },
    ],
    children: [
      {
        path: '',
        name: 'InvestView',
        component: () => import('@/modules/flokee/views/common/InvestView.vue'),
      },
    ],
  },
  {
    path: 'widgets',
    beforeEnter: [
      authMiddlewares.isAuthenticated,
      authMiddlewares.isAccountConfigured,
      authMiddlewares.enforceBusinessUnit,
    ],
    component: () => import('@/modules/flokee/layouts/DashboardLayout.vue'),
    children: [
      {
        meta: { title: 'nav_menu.widgets' },
        path: '',
        name: 'WidgetsView',
        component: () => import('@/modules/flokee/views/widgets/WidgetsView.vue'),
      },
      {
        meta: { title: 'widgets.chatpop.title' },
        path: 'chatpop',
        name: 'WidgetsChatpopView',
        component: () => import('@/modules/flokee/views/widgets/WidgetsChatpopView.vue'),
      },
    ],
  },
  {
    path: 'store',
    component: () => import('@/modules/flokee/layouts/DashboardLayout.vue'),
    beforeEnter: [
      authMiddlewares.isAuthenticated,
      authMiddlewares.isAccountConfigured,
      authMiddlewares.enforceBusinessUnit,
    ],
    children: [
      {
        path: 'quality',
        name: 'StoreQualityView',
        meta: { title: 'nav_menu.store_quality' },
        component: () => import('@/modules/flokee/views/store/StoreQualityView.vue'),
      },
    ],
  },
  {
    path: 'store/settings',
    component: () => import('@/modules/flokee/layouts/DashboardLayout.vue'),
    beforeEnter: [
      authMiddlewares.isAuthenticated,
      authMiddlewares.isAccountConfigured,
      authMiddlewares.enforceBusinessUnit,
    ],
    redirect: 'store/settings/general',
    children: [
      {
        path: 'general',
        name: 'StoreSettingsGeneralView',
        meta: { title: 'store_settings.nav_menu.general' },
        component: () =>
          import('@/modules/flokee/views/store-settings/StoreSettingsGeneralView.vue'),
      },
      {
        path: 'connections',
        name: 'StoreSettingsConnectionsView',
        meta: { title: 'store_settings.nav_menu.connections' },
        component: () =>
          import('@/modules/flokee/views/store-settings/StoreSettingsConnectionsView.vue'),
      },
      {
        path: 'notifications',
        name: 'StoreSettingsNotificationsView',
        meta: { title: 'store_settings.nav_menu.notifications' },
        component: () =>
          import('@/modules/flokee/views/store-settings/StoreSettingsNotificationsView.vue'),
      },
      {
        path: 'billing',
        name: 'StoreSettingsBillingView',
        beforeEnter: [ecommerceMiddlewares.isPaid],
        meta: { title: 'store_settings.nav_menu.billing' },
        component: () =>
          import('@/modules/flokee/views/store-settings/StoreSettingsBillingView.vue'),
      },
    ],
  },
  {
    path: 'store/connections',
    component: () => import('@/layouts/BlankLayout.vue'),
    beforeEnter: [
      authMiddlewares.isAuthenticated,
      authMiddlewares.isAccountConfigured,
      authMiddlewares.enforceBusinessUnit,
    ],
    redirect: 'store/settings/connections',
    children: [
      {
        path: 'diagnostics/:product',
        name: 'GoogleDiagnosticView',
        meta: { title: 'store_connections.diagnostics.title_suffix' },
        component: () =>
          import('@/modules/flokee/views/store-connections/GoogleDiagnosticView.vue'),
        props: true,
      },
      {
        path: 'tag-manager',
        meta: { title: 'store_connections.tag_manager.setup.title' },
        beforeEnter: [ecommerceMiddlewares.isPaid],
        children: [
          {
            path: '',
            name: 'StoreConnectionsTagManagerSetupView',
            component: () =>
              import('@/modules/flokee/views/store-connections/tag-manager/StoreConnectionsTagManagerSetupView.vue'),
          },
          {
            path: 'internal',
            name: 'StoreConnectionsTagManagerInternalView',
            component: () =>
              import('@/modules/flokee/views/store-connections/tag-manager/StoreConnectionsTagManagerInternalView.vue'),
          },
          {
            path: 'external',
            name: 'StoreConnectionsTagManagerExternalView',
            component: () =>
              import('@/modules/flokee/views/store-connections/tag-manager/StoreConnectionsTagManagerExternalView.vue'),
          },
        ],
      },
      {
        path: 'analytics',
        meta: { title: 'store_connections.analytics.setup.title' },
        children: [
          {
            path: '',
            name: 'StoreConnectionsAnalyticsSetupView',
            component: () =>
              import('@/modules/flokee/views/store-connections/analytics/StoreConnectionsAnalyticsSetupView.vue'),
          },
          {
            path: 'internal',
            name: 'StoreConnectionsAnalyticsInternalView',
            component: () =>
              import('@/modules/flokee/views/store-connections/analytics/StoreConnectionsAnalyticsInternalView.vue'),
          },
          {
            path: 'external',
            children: [
              {
                path: '',
                name: 'StoreConnectionsAnalyticsExternalView',
                component: () =>
                  import('@/modules/flokee/views/store-connections/analytics/StoreConnectionsAnalyticsExternalView.vue'),
              },
              {
                path: 'callback',
                name: 'StoreConnectionsAnalyticsExternalCallbackView',
                component: () =>
                  import('@/modules/flokee/views/store-connections/analytics/StoreConnectionsAnalyticsExternalCallbackView.vue'),
              },
            ],
          },
        ],
      },
      {
        path: 'merchant',
        meta: { title: 'store_connections.merchant.setup.title' },
        beforeEnter: [ecommerceMiddlewares.isPaid],
        children: [
          {
            path: '',
            name: 'StoreConnectionsMerchantSetupView',
            component: () =>
              import('@/modules/flokee/views/store-connections/merchant/StoreConnectionsMerchantSetupView.vue'),
          },
          {
            path: 'internal',
            name: 'StoreConnectionsMerchantInternalView',
            component: () =>
              import('@/modules/flokee/views/store-connections/merchant/StoreConnectionsMerchantInternalView.vue'),
          },
          {
            path: 'external',
            name: 'StoreConnectionsMerchantExternalView',
            component: () =>
              import('@/modules/flokee/views/store-connections/merchant/StoreConnectionsMerchantExternalView.vue'),
          },
        ],
      },
    ],
  },
  {
    path: 'auth',
    component: () => import('@/layouts/BlankLayout.vue'),
    redirect: 'auth/login',
    meta: { title: 'auth.sign_in.title' },
    beforeEnter: [authMiddlewares.isAppLogin],
    children: [
      {
        path: 'login',
        name: 'AuthLoginView',
        meta: { title: 'auth.sign_in.title' },
        beforeEnter: [authMiddlewares.shouldRedirectToDashboard],
        component: () => import('@/modules/flokee/views/auth/AuthLoginView.vue'),
      },
      {
        path: 'register',
        name: 'AuthRegisterView',
        meta: { title: 'auth.sign_up.title' },
        beforeEnter: [authMiddlewares.shouldRedirectToDashboard],
        component: () => import('@/modules/flokee/views/auth/AuthRegisterView.vue'),
      },
      {
        path: 'email',
        name: 'AuthEmailView',
        meta: { title: 'auth.sign_in_email.title' },
        beforeEnter: [authMiddlewares.shouldRedirectToDashboard],
        props: (route) => ({
          email: route.query.email ?? '',
        }),
        component: () => import('@/modules/flokee/views/auth/AuthEmailView.vue'),
      },
      {
        path: 'recover-password',
        meta: { title: 'auth.recover_password.title' },
        component: () => import('@/layouts/BaseLayout.vue'),
        children: [
          {
            path: '',
            name: 'AuthRecoverPasswordView',
            props: (route) => ({
              email: route.query.email ?? '',
            }),
            component: () => import('@/modules/flokee/views/auth/AuthRecoverPasswordView.vue'),
          },
        ],
      },
      {
        path: 'reset-password',
        meta: { title: 'auth.reset_password.title' },
        component: () => import('@/layouts/BaseLayout.vue'),
        children: [
          {
            path: '',
            name: 'AuthResetPasswordView',
            props: (route) => ({
              token: route.query.token ?? '',
            }),
            component: () => import('@/modules/flokee/views/auth/AuthResetPasswordView.vue'),
          },
        ],
      },
      {
        path: 'tienda-nube',
        component: () => import('@/layouts/BaseLayout.vue'),
        beforeEnter: [authMiddlewares.verifyAuthentication],
        children: [
          {
            path: 'callback',
            name: 'AuthTiendanubeCallbackView',
            props: (route) => ({
              code: route.query.code ?? '',
            }),
            component: () => import('@/modules/flokee/views/auth/AuthTiendanubeCallbackView.vue'),
          },
        ],
      },
    ],
  },
  {
    path: 'wizard',
    component: () => import('@/layouts/BlankLayout.vue'),
    redirect: { name: 'DashboardView' },
    beforeEnter: [authMiddlewares.isAuthenticated],
    children: [
      {
        path: 'create-account',
        name: 'WizardCreateAccountView',
        props: (route) => ({
          create: route.query.create,
        }),
        component: () => import('@/modules/flokee/views/wizard/WizardCreateAccountView.vue'),
      },
      {
        path: 'create-ecommerce',
        name: 'WizardCreateEcommerceView',
        component: () => import('@/modules/flokee/views/wizard/WizardCreateEcommerceView.vue'),
      },
      {
        path: 'select-ecommerce-provider',
        name: 'WizardSelectEcommerceProviderView',
        component: () =>
          import('@/modules/flokee/views/wizard/WizardSelectEcommerceProviderView.vue'),
      },
      {
        path: 'import-ecommerce',
        name: 'WizardImportEcommerceView',
        component: () => import('@/modules/flokee/views/wizard/WizardImportEcommerceView.vue'),
      },
    ],
  },
  {
    path: 'select-account',
    component: () => import('@/layouts/BlankLayout.vue'),
    beforeEnter: [authMiddlewares.isAuthenticated],
    meta: { title: 'select_account.description' },
    children: [
      {
        path: '',
        name: 'SelectAccountView',
        component: () => import('@/modules/flokee/views/common/SelectAccountView.vue'),
      },
    ],
  },
] satisfies RouteRecordRaw[];
